import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";

const selectCls = "w-full h-10 rounded-md border border-input bg-background px-3 text-sm";

export function AttachBookDialog({ saga, books, onClose, onDone }) {
  const [bookId, setBookId] = useState("");
  const [pos, setPos] = useState(1);
  const [busy, setBusy] = useState(false);
  const count = saga?.books.length || 0;

  useEffect(() => { if (saga) { setBookId(""); setPos(count + 1); } }, [saga, count]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/series/${saga.id}/attach`, { project_id: bookId, position: Number(pos) });
      const bits = [`added as Book ${data.series_order}`];
      if (data.recap_added) bits.push("story-so-far recap added");
      if (data.carried_entities) bits.push(`${data.carried_entities} Codex entr${data.carried_entities === 1 ? "y" : "ies"} carried in`);
      toast.success(`Book ${bits.join(" · ")}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add the book"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!saga} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="bg-popover" data-testid="attach-book-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Add an existing book to {saga?.title}</DialogTitle>
          <DialogDescription>
            The book's chapters, settings and existing Codex stay exactly as they are. It only gets a story-so-far
            recap if it doesn't already have one, plus any Codex entries from earlier books that it's missing.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Book</Label>
            <select value={bookId} onChange={(e) => setBookId(e.target.value)} className={selectCls} data-testid="attach-book-select">
              <option value="">Choose a standalone book…</option>
              {books.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
            {books.length === 0 && <p className="text-xs text-muted-foreground" data-testid="attach-book-none">No standalone books — every book is already in a saga.</p>}
          </div>
          <div className="space-y-2">
            <Label>Position in the saga</Label>
            <select value={pos} onChange={(e) => setPos(e.target.value)} className={selectCls} data-testid="attach-book-position">
              {Array.from({ length: count + 1 }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>{`Book ${n}${n === count + 1 ? " (at the end)" : ` (before “${saga.books[n - 1]?.title}”)`}`}</option>
              ))}
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !bookId} className="gap-2" data-testid="attach-book-submit">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} {busy ? "Adding…" : "Add to saga"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
